'use strict';

define('vb/private/services/serviceResolverFactory',[
  'vb/private/constants',
  'vb/private/utils',
  'vb/private/services/serviceResolver',
], (
  Constants,
  Utils,
  ServiceResolver,
) => {
  /**
   * @typedef {Object} CallingContext
   * @property {string} extensionId - The calling context extension ID
   */

  /**
   * @typedef {import('vb/private/services/services').default} Services
   * @typedef {import('vb/private/stateManagement/application').default} Application
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {Container|ServiceResolver|CallingContext} ServiceContext
   */

  const factoryID = Symbol('vb.service.resolver.factoryId');

  /**
   *
   */
  class ServiceResolverFactory {
    /**
     * Returns a service resolver for the given extension namespace.
     *
     * @param {string} [extensionId=Constants.ExtensionNamespaces.BASE]
     * @returns {ServiceResolver}
     */
    static getResolverForExtension(extensionId = Constants.ExtensionNamespaces.BASE) {
      const services = ServiceResolverFactory.application
        && ServiceResolverFactory.application.getExtensionServices(extensionId);
      return new ServiceResolver({ factoryID, extensionId, services });
    }

    /**
     * Returns a service resolver for the given container context.
     *
     * @param {Container} container
     * @returns {ServiceResolver}
     */
    static getResolverForContainer(container) {
      const app = container.application || ServiceResolverFactory.application;

      // TODO: simply
      const extensionId = Utils.getCallingExtensionId(container);
      const services = app && app.getExtensionServices(extensionId);

      const containerExtId = container.extensionId;
      let altServices;
      if (containerExtId !== extensionId) {
        altServices = app && app.getExtensionServices(containerExtId);
      }

      return new ServiceResolver({
        factoryID, extensionId, services, altServices,
      });
    }

    /**
     * Returns a service resolver for the given service context.
     * If no context is provided, the base extension resolver is returned.
     *
     * @param {string|ServiceContext} [serviceContext]
     * @returns {ServiceResolver}
     */
    static getResolver(serviceContext) {
      if (serviceContext) {
        if (ServiceResolverFactory.checkResolver(serviceContext)) {
          return /** @type {ServiceResolver} */ (serviceContext);
        }

        if (typeof serviceContext === 'string') {
          return ServiceResolverFactory.getResolverForExtension(serviceContext);
        }

        const container = /** @type {Container} */ (serviceContext);
        return ServiceResolverFactory.getResolverForContainer(container);
      }
      return ServiceResolverFactory.getResolverForExtension(Constants.ExtensionNamespaces.BASE);
    }

    /**
     * Check if the given object was created by ServiceResolverFactory.getResolver().
     *
     * @param {*} serviceResolver
     * @returns {boolean}
     */
    static checkResolver(serviceResolver) {
      // check we created it
      return serviceResolver && (serviceResolver.factoryID === factoryID);
    }

    /**
     * Returns the extension namespace for the given service context.
     * In strict mode, the context must be a ServiceResolver created by this factory.
     *
     * @param {ServiceContext} serviceContext
     * @param {boolean} [strict=false]
     * @returns {string}
     * @throws When strict parameter is true and serviceContext is not created by this class.
     */
    static getNamespace(serviceContext, strict = false) {
      const validServiceResolver = ServiceResolverFactory.checkResolver(serviceContext);
      // Note that during VBS-29855 investigation it was identified that the vbContext AggregateServiceDataProvider
      // receives is writable indicating someone is cloning it somewhere on the stack.
      // We need to be careful handling that and transitioning clients of that kind of behavior.
      if (strict && !validServiceResolver) {
        throw new Error('Invalid service resolver object');
      }

      if (serviceContext) {
        // we already check this so use it if it is true
        if (validServiceResolver) {
          return ServiceResolver.getNamespace(serviceContext);
        }

        if (serviceContext.extensionId) {
          return serviceContext.extensionId;
        }
      }
      return Constants.ExtensionNamespaces.BASE;
    }

    /**
     * Returns the services object for the given service context's namespace.
     *
     * @param {ServiceContext} serviceContext
     * @returns {Services|undefined}
     */
    static getServices(serviceContext) {
      const ns = ServiceResolverFactory.getNamespace(serviceContext);
      return ServiceResolverFactory.application
        && ServiceResolverFactory.application.getExtensionServices(ns);
    }
  }
  // will be set by the app singleton
  /**
   * @type {Application|undefined}
   */
  ServiceResolverFactory.application = undefined;

  return ServiceResolverFactory;
});

